import React from 'react';
import { Box, Stack, Tooltip } from '@mui/material';
import { theme } from '../../../../../../common/styles/theme';
import { Text } from '../../../../../../common/components/Text/Text';
import { Paper } from '../../../../../../common/components/Paper/Paper';
import { TextUtils } from '../../../../../../common/utils/text-utils';
import { SPEAKER_COLORS } from '../../../../CaptionsContainer/TargetCaptions/constants';
import { ICaptionsItem } from '../../../../../types/common';
import { ILessonTranscriptProps, findCaptionIndexAt } from './types';
import { CLASS_PLAY_CONTROL } from '../../Audio/components/constants';
import { PlayIcon } from '../../../../../../../icons/PlayIcon';
import { PauseIcon } from '../../../../../../../icons/PauseIcon';

const formatTime = (seconds: number): string => {
	const total = Math.max(0, Math.floor(seconds || 0));
	const m = Math.floor(total / 60);
	const s = total % 60;
	return `${m}:${s.toString().padStart(2, '0')}`;
};

const CLASS_PLAY_STATE_ICON = 'lesson-transcript-play-state';
const CLASS_PLAY_STATE_UNDER = 'lesson-transcript-play-state-under';

const playStateIconSx = {
	position: 'absolute',
	top: '50%',
	left: '50%',
	transform: 'translate(-50%, -50%)',
	width: theme.spacing(1.25),
	height: theme.spacing(1.25),
	color: theme.palette.primary.main,
};

// Play/pause mark drawn over a line's initials (or its time, when there's no speaker) so the text doesn't shift.
const PlayStateSlot: React.FC<{ isPlaying: boolean | null, children: React.ReactNode }> = ({ isPlaying, children }) => {
	if (isPlaying === null) return <>{children}</>;
	return (
		<Box component={'span'} sx={{ position: 'relative' }}>
			{isPlaying
				? <PauseIcon className={CLASS_PLAY_STATE_ICON} sx={playStateIconSx} />
				: <PlayIcon className={CLASS_PLAY_STATE_ICON} sx={playStateIconSx} />
			}
			<Box component={'span'} className={CLASS_PLAY_STATE_UNDER} sx={{ visibility: 'hidden' }}>{children}</Box>
		</Box>
	);
};

// same speaker keying as the main transcript (TargetCaptions), so each speaker keeps the same color
const getSpeakerKey = (caption: ICaptionsItem): string => {
	if (!caption.userName) return '';
	return caption.userId ? `id:${caption.userId}` : `name:${caption.userName}`;
};

// Transcript beside the Exercises/Words tabs — a timestamp clicked there highlights its line here;
// clicking a line plays it.
export const LessonTranscript: React.FC<ILessonTranscriptProps> = ({ captions, isPlaying, highlight, onLineClick }) => {

	const lineRefs = React.useRef<Array<HTMLElement | null>>([]);

	const speakerOrder = React.useMemo(() => {
		const order: string[] = [];
		captions.forEach(c => {
			const key = getSpeakerKey(c);
			if (key && !order.includes(key)) order.push(key);
		});
		return order;
	}, [captions]);

	const highlightIndex = React.useMemo(() => {
		if (!highlight) return -1;
		return 'index' in highlight ? highlight.index : findCaptionIndexAt(captions, highlight.time);
	}, [captions, highlight]);

	React.useEffect(() => {
		// a clicked line is already under the cursor — centering it would shift the list
		if (!highlight || 'index' in highlight) return;
		const el = lineRefs.current[highlightIndex];
		if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
	}, [highlight, highlightIndex]);

	return (
		<Paper sx={{ flex: '1 1 0', minWidth: 0, minHeight: 0, overflowY: 'auto', p: theme.spacing(1), background: theme.palette.grey[50] }}>
			<Stack gap={.75}>
				{captions.map((caption, index) => {
					const speakerIndex = speakerOrder.indexOf(getSpeakerKey(caption));
					const isHighlighted = index === highlightIndex;
					return (
						<Box
							key={index}
							ref={(el: HTMLElement | null) => { lineRefs.current[index] = el; }}
							className={CLASS_PLAY_CONTROL}
							onClick={() => onLineClick(index)}
							sx={{
								p: theme.spacing(.75, 1.25),
								borderRadius: theme.spacing(1),
								background: isHighlighted ? theme.palette.primary[100] : theme.palette.common.white,
								boxShadow: isHighlighted ? `inset 0 0 0 1px ${theme.palette.primary.main}` : 'none',
								transition: 'background .3s ease',
								cursor: 'pointer',
								...(!isHighlighted && { '&:hover': { background: theme.palette.grey[100] } }),
								// hovering the line brings the initials back from under the mark
								[`&:hover .${CLASS_PLAY_STATE_ICON}`]: { display: 'none' },
								[`&:hover .${CLASS_PLAY_STATE_UNDER}`]: { visibility: 'visible' },
							}}
						>
							{/* the highlighted line is the one a click plays; clicking it again while playing stops it */}
							<Text
								component={'span'}
								sx={{
									mr: 1,
									color: theme.palette.grey[400],
									fontSize: theme.typography.subtitle1,
								}}
							>
								{isHighlighted && speakerIndex < 0
									? <PlayStateSlot isPlaying={isPlaying}>{formatTime(caption.startTime)}</PlayStateSlot>
									: formatTime(caption.startTime)
								}
							</Text>
							{speakerIndex >= 0 &&
							<Tooltip
								title={caption.userName}
							>
								<Text
									component={'span'}
										sx={{
											mr: 1,
											fontWeight: 600,
											fontSize: '11px',
											color: SPEAKER_COLORS[speakerIndex % SPEAKER_COLORS.length],
										}}
								>
									{isHighlighted
										? <PlayStateSlot isPlaying={isPlaying}>{TextUtils.getInitials(caption.userName!)}</PlayStateSlot>
										: TextUtils.getInitials(caption.userName!)
									}
								</Text>
							</Tooltip>
							}
							<Text
								component={'span'}
								sx={{
									fontSize: theme.typography.subtitle2,
								}}
							>
								{caption.text}
							</Text>
						</Box>
					);
				})}
			</Stack>
		</Paper>
	);
};
